import React, { useState } from 'react';
import './FileBrowser.css';
import VideoThumbnail from './VideoThumbnail';
import fileIcon from '../img/file.png';
import folderIcon from '../img/folder-purple.png';
import cdIcon from '../img/cd-icon.png';
import photoboothIcon from '../img/photobooth22.png';
import gardenIcon from '../img/garden.png';
import trashIcon from '../img/trash.png';
import geniusIcon from '../img/genius.png';
import terminalIcon from '../img/terminal.svg';

// Import media files from "in orbit" folder
// 01 - la paix
import laPaixMp3 from '../browser/in orbit/01 - la paix/la paix v1.mp3';
import meChloGif from '../browser/in orbit/01 - la paix/me&chlo.gif';
import btsMp4 from '../browser/in orbit/01 - la paix/bts.mp4';
import willypizzaJpg from '../browser/in orbit/01 - la paix/willypizza.jpg';
import noiseMov from '../browser/in orbit/01 - la paix/noise.mov';

// 02 - your orbit
import oopsJpg from '../browser/in orbit/02 - your orbit/oops.jpg';
import sorryEmilMp4 from '../browser/in orbit/02 - your orbit/sorry_emil.mp4';
import masalaMixtapeMp4 from '../browser/in orbit/02 - your orbit/masala_mixtape.mp4';
import mm1Jpg from '../browser/in orbit/02 - your orbit/mm1.jpg';
import mm2Jpg from '../browser/in orbit/02 - your orbit/mm2.jpg';
import nubluLesJpg from '../browser/in orbit/02 - your orbit/nublu_les.jpg';
import yourOrbitLyrics from '../browser/in orbit/02 - your orbit/lyrics.txt';

// 03 - things change
import thingsChangeLyrics from '../browser/in orbit/03 - things change/lyrics.txt';

// 04 - everytime
import everytimeLyrics from '../browser/in orbit/04 - everytime/lyrics.txt';

// 05 - need ya
import prateekStudioMov from '../browser/in orbit/05 - need ya/prateek_studio.MOV';
import karthikEmilGif from '../browser/in orbit/05 - need ya/karthik&emil.gif';
import needYaLyrics from '../browser/in orbit/05 - need ya/lyrics.txt';
import needYaMp3 from '../browser/in orbit/05 - need ya/need ya [og:alt verse].mp3';

// 06- simmering
import bbdJpg from '../browser/in orbit/06- simmering/bbd.jpg';
import chloe10Jpg from '../browser/in orbit/06- simmering/chloe 10.8.2024.jpg';
import chloeOreoJpg from '../browser/in orbit/06- simmering/chloe_oreo.jpg';
import simmeringMp3 from '../browser/in orbit/06- simmering/simmering beat v1.mp3';

// In Orbit cover
import inOrbitCover from '../browser/in orbit/cover.jpg';

// Trash
import inYourEyesMp3 from '../browser/trash/in your eyes.mp3';
import lilUziRemixMp3 from '../browser/trash/lil uzi x dhar beats remix.mp3';

interface FileItem {
  name: string;
  type: 'folder' | 'file' | 'application';
  icon: string | any;
  filePath?: string; // Add filePath to store the imported asset
  action?: () => void;
}

interface FileBrowserProps {
  initialPath?: string;
  windowId?: string;
  onOpenFolder?: (folderName: string, x?: number, y?: number, parentWindowId?: string) => void;
  onOpenFile?: (fileName: string, filePath: string, x?: number, y?: number) => void;
  onLaunchApp?: (appName: string, x?: number, y?: number) => void;
  onOpenPreview?: (fileName: string, filePath: string, fileType: 'image' | 'mp3' | 'video', x?: number, y?: number) => void;
  onPlaySong?: (trackIndex: number) => void;
}

const FileBrowser: React.FC<FileBrowserProps> = ({
  initialPath = 'File Browser',
  windowId,
  onOpenFolder,
  onOpenFile,
  onLaunchApp,
  onOpenPreview,
  onPlaySong
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  
  // Map song folder names to track indices (01-06 maps to 0-5)
  const songFolderToTrackIndex: Record<string, number> = {
    '01 - la paix': 0,
    '02 - your orbit': 1,
    '03 - things change': 2,
    '04 - everytime': 3,
    '05 - need ya': 4,
    '06- simmering': 5
  };
  
  // Check if current path is a song folder
  const isSongFolder = initialPath in songFolderToTrackIndex;
  const trackIndex = isSongFolder ? songFolderToTrackIndex[initialPath] : null;
  
  const fileStructure: Record<string, FileItem[]> = {
    'File Browser': [
      { name: 'Applications', type: 'folder', icon: folderIcon },
      { name: 'Desktop', type: 'folder', icon: folderIcon },
      { name: 'In Orbit', type: 'folder', icon: folderIcon },
      { name: 'Trash', type: 'folder', icon: trashIcon }
    ],
    Applications: [
      { name: 'Music Player', type: 'application', icon: cdIcon },
      { name: 'Photo Booth', type: 'application', icon: photoboothIcon },
      { name: 'Garden', type: 'application', icon: gardenIcon },
      { name: 'Text Editor', type: 'application', icon: fileIcon },
      { name: 'Terminal', type: 'application', icon: terminalIcon }
    ],
    Desktop: [
      { name: 'File Browser', type: 'folder', icon: folderIcon },
      { name: 'credits.txt', type: 'file', icon: fileIcon },
      { name: 'Music Player', type: 'application', icon: cdIcon },
      { name: 'Photo Booth', type: 'application', icon: photoboothIcon },
      { name: 'Garden', type: 'application', icon: gardenIcon },
      { name: 'Trash', type: 'folder', icon: trashIcon }
    ],
    'In Orbit': [
      { name: '01 - la paix', type: 'folder', icon: folderIcon },
      { name: '02 - your orbit', type: 'folder', icon: folderIcon },
      { name: '03 - things change', type: 'folder', icon: folderIcon },
      { name: '04 - everytime', type: 'folder', icon: folderIcon },
      { name: '05 - need ya', type: 'folder', icon: folderIcon },
      { name: '06- simmering', type: 'folder', icon: folderIcon },
      { name: 'cover.jpg', type: 'file', icon: inOrbitCover, filePath: inOrbitCover }
    ],
    '01 - la paix': [
      { name: 'la paix v1.mp3', type: 'file', icon: cdIcon, filePath: laPaixMp3 },
      { name: 'me&chlo.gif', type: 'file', icon: meChloGif, filePath: meChloGif },
      { name: 'bts.mp4', type: 'file', icon: btsMp4, filePath: btsMp4 },
      { name: 'willypizza.jpg', type: 'file', icon: willypizzaJpg, filePath: willypizzaJpg },
      { name: 'noise.mov', type: 'file', icon: noiseMov, filePath: noiseMov }
    ],
    '02 - your orbit': [
      { name: 'oops.jpg', type: 'file', icon: oopsJpg, filePath: oopsJpg },
      { name: 'sorry_emil.mp4', type: 'file', icon: sorryEmilMp4, filePath: sorryEmilMp4 },
      { name: 'masala_mixtape.MP4', type: 'file', icon: masalaMixtapeMp4, filePath: masalaMixtapeMp4 },
      { name: 'mm1.JPG', type: 'file', icon: mm1Jpg, filePath: mm1Jpg },
      { name: 'mm2.JPG', type: 'file', icon: mm2Jpg, filePath: mm2Jpg },
      { name: 'nublu_les.jpg', type: 'file', icon: nubluLesJpg, filePath: nubluLesJpg },
      { name: 'lyrics.txt', type: 'file', icon: fileIcon, filePath: yourOrbitLyrics }
    ],
    '03 - things change': [
      { name: 'lyrics.txt', type: 'file', icon: fileIcon, filePath: thingsChangeLyrics }
    ],
    '04 - everytime': [
      { name: 'lyrics.txt', type: 'file', icon: fileIcon, filePath: everytimeLyrics }
    ],
    '05 - need ya': [
      { name: 'Genius', type: 'application', icon: geniusIcon },
      { name: 'karthik&emil.gif', type: 'file', icon: karthikEmilGif, filePath: karthikEmilGif },
      { name: 'need ya v1.mp3', type: 'file', icon: cdIcon, filePath: needYaMp3 },
      { name: 'prateek_studio.MOV', type: 'file', icon: prateekStudioMov, filePath: prateekStudioMov },
      { name: 'lyrics.txt', type: 'file', icon: fileIcon, filePath: needYaLyrics }
    ],
    '06- simmering': [
      { name: 'bbd.jpg', type: 'file', icon: bbdJpg, filePath: bbdJpg },
      { name: 'chloe 10.8.2024.jpg', type: 'file', icon: chloe10Jpg, filePath: chloe10Jpg },
      { name: 'chloe_oreo.jpg', type: 'file', icon: chloeOreoJpg, filePath: chloeOreoJpg },
      { name: 'simmering beat v1.mp3', type: 'file', icon: cdIcon, filePath: simmeringMp3 }
    ],
    Trash: [
      { name: 'abandoned.txt', type: 'file', icon: fileIcon },
      { name: 'lil uzi x dhar beats remix.mp3', type: 'file', icon: cdIcon, filePath: lilUziRemixMp3 },
      { name: 'in your eyes.mp3', type: 'file', icon: cdIcon, filePath: inYourEyesMp3 }
    ]
  };

  const currentItems = fileStructure[initialPath] || [];

  // Detect if device supports touch
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const handleItemClick = (item: FileItem, index: number, event: React.MouseEvent) => {
    // On touch devices, highlight then open after brief delay
    if (isTouchDevice) {
      setSelectedIndex(index); // Highlight it
      // Delay opening to show the highlight effect
      setTimeout(() => {
        handleItemDoubleClick(item, index, event); // Then open it
      }, 150);
    } else {
      setSelectedIndex(index);
    }
  };

  const getFileType = (fileName: string): 'image' | 'mp3' | 'video' | 'text' | null => {
    const extension = fileName.split('.').pop()?.toLowerCase();

    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
    const mp3Extensions = ['mp3'];
    const videoExtensions = ['mov', 'mp4', 'avi', 'webm', 'mkv'];
    const textExtensions = ['txt', 'md', 'json', 'js', 'ts', 'tsx', 'jsx', 'css', 'html'];

    if (extension && imageExtensions.includes(extension)) return 'image';
    if (extension && mp3Extensions.includes(extension)) return 'mp3';
    if (extension && videoExtensions.includes(extension)) return 'video';
    if (extension && textExtensions.includes(extension)) return 'text';

    return null;
  };

  const handleItemDoubleClick = (item: FileItem, index: number, event: React.MouseEvent) => {
    setSelectedIndex(null); // Clear selection

    if (item.type === 'folder') {
      // Get click coordinates relative to the desktop container
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      const desktopElement = document.querySelector('.desktop-content');
      const desktopRect = desktopElement?.getBoundingClientRect();

      if (desktopRect) {
        const iconCenterX = rect.left + rect.width / 2 - desktopRect.left;
        const iconCenterY = rect.top + rect.height / 2 - desktopRect.top;
        onOpenFolder?.(item.name, iconCenterX, iconCenterY, windowId);
      } else {
        onOpenFolder?.(item.name, undefined, undefined, windowId);
      }
    } else if (item.type === 'file') {
      // Get coordinates for file opening animation
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      const desktopElement = document.querySelector('.desktop-content');
      const desktopRect = desktopElement?.getBoundingClientRect();
      const iconCenterX = desktopRect ? rect.left + rect.width / 2 - desktopRect.left : undefined;
      const iconCenterY = desktopRect ? rect.top + rect.height / 2 - desktopRect.top : undefined;

      // Determine file type and call appropriate handler
      const fileType = getFileType(item.name);

      if (fileType === 'image' || fileType === 'mp3' || fileType === 'video') {
        // Use the imported filePath from the item
        const filePath = item.filePath || '';
        onOpenPreview?.(item.name, filePath, fileType, iconCenterX, iconCenterY);
      } else {
        // Default to text editor for other files
        const filePath = item.filePath || '';
        onOpenFile?.(item.name, filePath, iconCenterX, iconCenterY);
      }
    } else if (item.type === 'application') {
      // Get coordinates for application launch animation
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      const desktopElement = document.querySelector('.desktop-content');
      const desktopRect = desktopElement?.getBoundingClientRect();

      if (desktopRect) {
        const iconCenterX = rect.left + rect.width / 2 - desktopRect.left;
        const iconCenterY = rect.top + rect.height / 2 - desktopRect.top;
        onLaunchApp?.(item.name, iconCenterX, iconCenterY);
      } else {
        onLaunchApp?.(item.name);
      }
    }
  };

  const handlePlaySong = () => {
    if (trackIndex !== null && onPlaySong) {
      onPlaySong(trackIndex);
    }
  };

  return (
    <div className="file-browser">
      <div className="file-browser-header">
        <div className="folder-title">{initialPath}</div>
        {isSongFolder && (
          <button className="play-song-button" onClick={handlePlaySong}>
            Play song
          </button>
        )}
      </div>
      <div className={`file-list ${initialPath === 'Trash' ? 'trash-layout' : ''}`}>
        {currentItems.map((item, index) => (
          <div
            key={index}
            className="file-browser-icon"
            onClick={(event) => handleItemClick(item, index, event)}
            onDoubleClick={(event) => handleItemDoubleClick(item, index, event)}
          >
            <div className="file-browser-icon-image">
              {(() => {
                const fileType = getFileType(item.name);
                const iconClassName = `file-browser-icon-img ${selectedIndex === index ? 'selected' : ''}`;

                // For video files, use VideoThumbnail component
                if (fileType === 'video' && item.filePath) {
                  return (
                    <VideoThumbnail
                      src={item.filePath}
                      alt={item.name}
                      className={iconClassName}
                    />
                  );
                }

                // For image files or other files with string paths
                if (typeof item.icon === 'string' && (item.icon.startsWith('/') || item.icon.includes('static') || item.icon.startsWith('data:'))) {
                  return (
                    <img
                      src={item.icon}
                      alt={item.name}
                      className={iconClassName}
                    />
                  );
                }

                // Default: render icon as element
                return <span className={selectedIndex === index ? 'selected' : ''}>{item.icon}</span>;
              })()}
            </div>
            <div className={`file-browser-icon-label ${selectedIndex === index ? 'selected' : ''}`}>
              {item.name}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FileBrowser;